/**
 * Melodimals monetization — single API for the whole app.
 *
 * Android (Tauri): AdMob (vendored fork, COPPA flags) + Play Billing
 * (tauri-plugin-iap). Web/desktop: all no-op (free outside Play for
 * now; Stripe/Paddle will arrive with the stores phase).
 *
 * The SDKs are imported with a dynamic `import()` BEHIND the platform gate:
 * they never enter the web bundle.
 *
 * ── Ads: the `growth` kit, not a homemade rule ───────────────────────────────
 * The ads policy does NOT live here: it lives in `growth/` (the portfolio kit,
 * canon listto) and is applied through `AdsController`. This module only wires
 * the real dependencies — the AdMob provider, `localStorage`, the current route and
 * the premium flag — and exposes the API the app consumes.
 *
 * What was there before was a counter of its own (`melodimals.ad-entries.v1`: an
 * interstitial every 3 entries, with 2 minutes of separation). It was more lax than
 * the child regime §6 in the two things that matter: it did not distinguish "entering a
 * module" from "being playing", and it had no daily cap. Now the verdict is
 * given by `adGate` with `CHILD_AD_CAP_POLICY` (1/session, 2/day) and the old key is left
 * orphaned on purpose: a counter whose semantics no longer exist is not migrated.
 */

import { adCapsFromConfig } from "./ad-policy";
import { AdsController } from "./ads-controller";
import { resolveAdsConfig } from "./growth/config";
import { MELODIMALS_AD_PLACEMENTS, MELODIMALS_AD_TIER } from "./growth/domain";
import { makeAdProvider } from "./growth/provider";
import type { AdCapPolicy, AdProvider, AdsConfig } from "./growth/types";
import { type IapConfig, resolveIapConfig } from "./iap-config";
import { isAndroid } from "./platform-gate";
import { createPremiumStore } from "./premium-store";

export { adCapsFromConfig } from "./ad-policy";
export { isAndroid, isTauriRuntime } from "./platform-gate";
export { AdsController } from "./ads-controller";
export * from "./growth";
export * from "./iap-config";

const premiumStore = createPremiumStore();
let initialized = false;
let controller: AdsController | undefined;

export const isPremium = (): boolean => premiumStore.isPremium();
export const subscribePremium = premiumStore.subscribe;

/**
 * Build ads configuration. `import.meta.env` is read ONCE: it is
 * static (Vite bakes it into the bundle), so re-reading it would add nothing.
 *
 * The tier is fixed to `K` in code, not by env: that Melodimals is a kids'
 * app is not a deployment decision. `resolveAdsConfig` derives from there
 * `childDirected: true` and `maxAdContentRating: "G"`, and no env can
 * relax it.
 */
export function melodimalsAdsConfig(
  env: Record<string, string | undefined> = buildEnv(),
): AdsConfig {
  return resolveAdsConfig({ ...env, VITE_ADS_TIER: MELODIMALS_AD_TIER });
}

/** Vite's `import.meta.env`, absent outside the bundler (tests, Node). */
function buildEnv(): Record<string, string | undefined> {
  const meta: ImportMeta & { env?: Record<string, string | undefined> } = import.meta;
  return meta.env ?? {};
}

/**
 * Build **native purchase** configuration. Off unless the build
 * declares `VITE_IAP_ENABLED`; see `iap-config.ts` for why the default and
 * the product id clamp.
 *
 * It is deliberately not cached in a module const: the env is read on every call
 * so tests can move it. In a real bundle the value is static
 * anyway (Vite bakes it), so re-reading it costs nothing.
 */
export function melodimalsIapConfig(
  env: Record<string, string | undefined> = buildEnv(),
): IapConfig {
  return resolveIapConfig(env);
}

/**
 * Can you buy inside this binary? It is consulted by the `/parents` UI
 * so as not to paint a button that cannot work. **It is not the only door**: the
 * real cutoff is in `initMonetization`/`purchaseRemoveAds` (below) and,
 * above all, in the native plugin registration in `src-tauri/src/lib.rs`, which is
 * the only thing that prevents the billing client from connecting to Play at
 * startup.
 */
export function isIapEnabled(): boolean {
  return melodimalsIapConfig().enabled;
}

/**
 * Creates the app's ads controller. Exported for the tests, which
 * inject a fake provider and a clock.
 */
export function createAdsController(
  overrides: {
    provider?: AdProvider;
    storage?: Pick<Storage, "getItem" | "setItem" | "removeItem">;
    isPremium?: () => boolean;
    currentPath?: () => string;
    policy?: () => Partial<AdCapPolicy>;
    now?: () => number;
  } = {},
): AdsController {
  const config = melodimalsAdsConfig();
  return new AdsController({
    provider: overrides.provider ?? makeAdProvider(config),
    storage: overrides.storage ?? localStorage,
    placements: MELODIMALS_AD_PLACEMENTS,
    isPremium: overrides.isPremium ?? (() => premiumStore.isPremium()),
    currentPath: overrides.currentPath ?? (() => globalThis.location?.pathname ?? "/"),
    // Capping by CONFIGURATION, not by constant: build env + the
    // adult's settings, re-read on every evaluation so the change applies
    // live. The child regime remains the floor (see `ad-policy.ts`).
    policy:
      overrides.policy ??
      (() => adCapsFromConfig(buildEnv(), globalThis.localStorage as Storage | undefined)),
    ...(overrides.now ? { now: overrides.now } : {}),
  });
}

/**
 * Initializes ads + IAP (Android only). Restores purchases: if "remove
 * ads" is purchased, premium becomes active on this device.
 *
 * Two independent switches: ads are governed by `AdsConfig`
 * (`VITE_ADS_ENABLED` + tier clamp) and the purchase by `IapConfig`
 * (`VITE_IAP_ENABLED`). Both off by default.
 */
export async function initMonetization(): Promise<void> {
  if (!isAndroid() || initialized) {
    return;
  }
  initialized = true;

  try {
    controller = createAdsController();
    await controller.init();
    // Preload only if there are ads to show: asking AdMob for
    // inventory for a premium user would be a network request that serves nobody.
    if (!premiumStore.isPremium()) {
      await controller.prewarm();
    }
  } catch (error) {
    console.warn("[monetization] ads init failed:", error);
  }

  // Native purchase: ONLY if the build declares it. Without this gate, `iap.initialize()`
  // was called on all Android, which is traffic against Play Billing at
  // the startup of a kids' app published "without in-binary purchase".
  const iapConfig = melodimalsIapConfig();
  if (!iapConfig.enabled) {
    return;
  }

  try {
    const iap = await import("@choochmeque/tauri-plugin-iap-api");
    await iap.initialize();
    const restored = await iap.restorePurchases("inapp");
    const owned = restored.purchases.some(
      (p) =>
        p.productId === iapConfig.removeAdsProductId &&
        p.purchaseState === iap.PurchaseState.PURCHASED,
    );
    if (owned) {
      premiumStore.setPremium(true);
      await controller?.onPremiumAcquired();
    }
  } catch (error) {
    console.warn("[monetization] IAP init failed:", error);
  }
}

/**
 * Interstitial on the scene→module transition. It resolves immediately (no ad)
 * outside Android, if premium, if the child is inside a game or if the
 * cap is not hit. **It never throws**: an ads failure cannot break
 * navigation.
 */
export async function maybeShowInterstitial(): Promise<void> {
  if (!controller || !isAndroid()) {
    return;
  }
  await controller.maybeShowInterstitial();
}

/**
 * The child finished playing a module. It is the "value unit" that §4 requires
 * before an interstitial is legal — without it the gate denies with
 * `no-value-unit`, which is exactly what prevents the ad before the first game.
 */
export function recordGamePlayed(): void {
  controller?.recordGamePlayed();
}

/** Shows the banner of a passive screen (`/progress`). */
export async function showPassiveBanner(): Promise<void> {
  if (!controller || !isAndroid()) {
    return;
  }
  await controller.showBanner();
}

/** Removes the banner on leaving the passive screen. */
export async function hidePassiveBanner(): Promise<void> {
  await controller?.hideBanner();
}

/** "Remove ads" purchase (ALWAYS call behind the parental gate). */
export async function purchaseRemoveAds(): Promise<boolean> {
  const { removeAdsProductId } = melodimalsIapConfig();
  // `removeAdsProductId` empty ⇒ the purchase is off in this build. The VALUE is
  // checked, not the flag: if one day someone deletes the `enabled`
  // gate, here there would still be no product to ask Play for.
  if (!isAndroid() || !removeAdsProductId) {
    return false;
  }
  try {
    const iap = await import("@choochmeque/tauri-plugin-iap-api");
    const purchase = await iap.purchase(removeAdsProductId, "inapp");
    if (purchase.purchaseState === iap.PurchaseState.PURCHASED) {
      await iap.acknowledgePurchase(purchase.purchaseToken);
      // Optimistic local flag: 0 ads NOW, without waiting for acknowledge/network
      // (§4, "post-purchase premium"). The live banner is removed on the spot.
      premiumStore.setPremium(true);
      await controller?.onPremiumAcquired();
      return true;
    }
    return false;
  } catch (error) {
    console.warn("[monetization] purchase failed:", error);
    return false;
  }
}

/** Re-syncs purchases manually (the "restore" button in /parents). */
export async function restorePremium(): Promise<boolean> {
  const { removeAdsProductId } = melodimalsIapConfig();
  if (!isAndroid() || !removeAdsProductId) {
    return false;
  }
  try {
    const iap = await import("@choochmeque/tauri-plugin-iap-api");
    const restored = await iap.restorePurchases("inapp");
    const owned = restored.purchases.some(
      (p) => p.productId === removeAdsProductId && p.purchaseState === iap.PurchaseState.PURCHASED,
    );
    premiumStore.setPremium(owned);
    if (owned) {
      await controller?.onPremiumAcquired();
    }
    return owned;
  } catch (error) {
    console.warn("[monetization] restore failed:", error);
    return false;
  }
}
